import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ExceptionsForm } from "./exceptions-form";
import { useUpdateExceptions } from "../../api/update-exceptions";

export const ExceptionsUpdate = ({ availability }) => {
    const sortAsc = (a, b) =>
        a.startDate.localeCompare(b.startDate) ||
        a.endDate.localeCompare(b.endDate);

    const exceptions = availability.filter(
        (x) =>
            !x.isDeleted &&
            x.startDate !== "1900-01-01" &&
            x.endDate !== "2097-01-01",
    );

    const dateGroups = {};

    const defaultValues = exceptions.reduce((acc, exception) => {
        acc.exceptions ??= {};

        // Group multiple time slots on the same date
        const groupKey = `${exception.startDate}_${exception.endDate}`;
        const ownerId = dateGroups[groupKey] ?? exception.availabilityGuid;
        dateGroups[groupKey] = ownerId;

        if (!acc.exceptions[ownerId]) {
            acc.exceptions[ownerId] = {
                enabled: !exception.isDisabled,
                Dates: [
                    {
                        startDate: exception.startDate,
                        endDate: exception.endDate,
                        allDay:
                            exception.startTime === "00:00" &&
                            exception.endTime === "00:00",
                    },
                ],
                Times: [],
            };
        }

        acc.exceptions[ownerId].Times.push({
            availabilityGuid: exception.availabilityGuid,
            startTime: exception.startTime,
            endTime: exception.endTime,
        });

        return acc;
    }, {});

    // Group time slots on same date for list
    const groupedExceptions = Object.entries(defaultValues.exceptions ?? {})
        .map(([id, exception]) => ({
            id,
            ...exception,
            startDate: exception.Dates[0].startDate,
            endDate: exception.Dates[0].endDate,
        }))
        .sort(sortAsc);

    const dateSlotSchema = yup.object({
        startDate: yup.string().required("Start date is required"),
        endDate: yup
            .string()
            .required("End date is required")
            .test(
                "is-after-start",
                "End date must be after start date",
                function (endDate) {
                    const { startDate } = this.parent;

                    return !startDate || !endDate || endDate >= startDate;
                },
            ),
        allDay: yup.boolean(),
    });

    const timeSlotSchema = yup.object({
        startTime: yup.string().required("Start time is required"),
        endTime: yup
            .string()
            .required("End time is required")
            .test(
                "is-after-start",
                "End time must be after start time",
                function (endTime) {
                    const { startTime } = this.parent;

                    return !startTime || !endTime || endTime > startTime;
                },
            ),
    });

    const exceptionSchema = yup.object({
        enabled: yup.boolean(),

        Dates: yup
            .array()
            .min(1, "Please add at least one date")
            .of(dateSlotSchema),

        Times: yup.array().when(["Dates"], {
            is: (dates) => !dates?.[0]?.allDay,
            then: (schema) =>
                schema
                    .min(1, "Please add at least one availability slot")
                    .of(timeSlotSchema)
                    .test(
                        "no-overlap",
                        "Time slots cannot overlap",
                        function (times) {
                            if (!times || times.length < 2) return true;

                            // Only consider complete slots
                            const valid = times
                                .map((t, index) => ({ ...t, index }))
                                .filter((t) => t.startTime && t.endTime);

                            const sorted = [...valid].sort((a, b) =>
                                a.startTime.localeCompare(b.startTime),
                            );

                            for (let i = 1; i < sorted.length; i++) {
                                const prev = sorted[i - 1];
                                const curr = sorted[i];

                                if (curr.startTime < prev.endTime) {
                                    // attach the error to final pair
                                    const target =
                                        curr.index > prev.index ? curr : prev;

                                    return this.createError({
                                        path: `${this.path}[${target.index}].startTime`,
                                        message: "Time slots cannot overlap",
                                    });
                                }
                            }

                            return true;
                        },
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const schema = yup.object({
        exceptions: yup.lazy((exceptions) =>
            yup.object(
                Object.keys(exceptions ?? {}).reduce((acc, id) => {
                    acc[id] = exceptionSchema;
                    return acc;
                }, {}),
            ),
        ),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        values: defaultValues,
        mode: "onChange",
    });

    const mutation = useUpdateExceptions();

    const onSubmit = (data) => {
        mutation.mutate({
            data,
        });
    };

    return (
        <>
            <h2 className="mb-3 text-xl font-semibold text-darkgreen-500">
                Time off
            </h2>
            <ExceptionsForm
                methods={methods}
                onSubmit={onSubmit}
                exceptions={groupedExceptions}
                isPending={mutation.isPending}
            />
        </>
    );
};
